import React from "react";

import { Button2 } from "@/ui2/atoms/Button";
import EnvelopeIcon from "@/assets/images/icons2/icon--envelope.svg?component";
import { formatMobileNumber } from "@/utils/formInputUtils";
import type { PatientDetails } from "@/core/types";
import PhoneIcon from "@/assets/images/icons2/icon--phone.svg?component";
import { showToast } from "@/providers/ToastProvider";
import { Tooltip2 } from "@/ui2/molecules/Tooltip";
import VerifiedIcon from "@/assets/images/icons2/icon--verified.svg?component";

type Props = {
  patientDetails: Pick<PatientDetails, "email" | "email_verified" | "mobile" | "preferences">;
};

export function PatientContact({ patientDetails }: Props) {
  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    showToast([{ heading: "Copied to clipboard.", variant: "success" }]);
  };

  return (
    <ul className="tw:flex tw:flex-col tw:items-start">
      <ContactDetail
        isVerified={patientDetails.preferences?.is_sms_verified}
        verifiedLabel="Verified phone number"
      >
        <ContactDetailPressable
          icon={<PhoneIcon />}
          onPress={() => handleCopy(patientDetails.mobile)}
          text={formatMobileNumber(patientDetails.mobile)}
        />
      </ContactDetail>

      <ContactDetail
        isVerified={patientDetails.email_verified}
        verifiedLabel="Verified email address"
      >
        <ContactDetailPressable
          icon={<EnvelopeIcon />}
          onPress={() => handleCopy(patientDetails.email)}
          text={patientDetails.email}
        />
      </ContactDetail>
    </ul>
  );
}

function ContactDetail({
  children,
  isVerified,
  verifiedLabel,
}: {
  children: React.ReactNode;
  isVerified: boolean;
  verifiedLabel: string;
}) {
  return (
    <li className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:border-t-stroke-quaternary tw:py-2 tw:not-first:border-t">
      {children}

      {isVerified && (
        <Tooltip2 text={verifiedLabel}>
          <VerifiedIcon className="tw:text-text-success-secondary" />
        </Tooltip2>
      )}
    </li>
  );
}

export function ContactDetailPressable({
  icon,
  onPress,
  text,
}: {
  icon: React.ReactNode;
  onPress: () => void;
  text: string;
}) {
  return (
    <Button2
      className="tw:flex tw:items-center tw:gap-2"
      onPress={onPress}
      rawChildren
      size="sm"
      variant="link"
    >
      {icon}
      <span className="trim-both tw:text-left tw:font-normal tw:break-all tw:whitespace-normal">
        {text}
      </span>
    </Button2>
  );
}
